import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, ArrowLeft, Star, Sparkles, Heart } from 'lucide-react';
import { Job } from '../types/Job';
import { getJobBySlug } from '../utils/storage';
import JobApplicationModal from '../components/JobApplicationModal';
import { getDefaultJobImage } from '../utils/jobImages';

const JobDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadJob = async () => {
      if (slug) {
        const foundJob = await getJobBySlug(slug);
        if (foundJob && foundJob.isActive) {
          setJob(foundJob);
        } else {
          navigate('/jobs');
        }
      }
    };
    loadJob();
  }, [slug, navigate]);

  if (!job) {
    return (
      <div className="container mx-auto px-4 py-12">
        <p className="text-center text-gray-600">Cargando...</p>
      </div>
    );
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'full-time': 'Tiempo Completo',
      'part-time': 'Tiempo Parcial',
      'contract': 'Contrato',
      'temporary': 'Temporal'
    };
    return labels[type] || type;
  };

  const headerImage = job.headerImage || getDefaultJobImage(job.title);

  return (
    <div>
      {job && (
        <JobApplicationModal
          job={job}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      <section className="relative h-[500px] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${headerImage}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-dark/90 via-primary/85 to-accent/90"></div>
        </div>
        <div className="container mx-auto px-4 pb-12 relative z-10 text-white">
          <Link to="/jobs" className="inline-flex items-center text-white hover:scale-105 transition-all mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a ofertas
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Star className="w-10 h-10 text-yellow-300" fill="currentColor" />
            <h1 className="text-5xl md:text-6xl font-extrabold drop-shadow-2xl">{job.title}</h1>
          </div>
          <p className="text-3xl md:text-4xl font-light drop-shadow-lg">{job.company}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="bg-white p-8 rounded-2xl shadow-lg mb-6 border-t-4 border-accent">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                  <Sparkles className="w-8 h-8 text-accent" />
                  Descripción del Puesto
                </h2>
                <p className="text-gray-700 text-lg whitespace-pre-wrap leading-relaxed">{job.description}</p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg mb-6 border-t-4 border-primary">
                <h2 className="text-3xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                  <Star className="w-8 h-8 text-primary" />
                  Requisitos
                </h2>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  {job.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))}
                </ul>
              </div>

              {job.benefits && job.benefits.length > 0 && (
                <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-accent-light">
                  <h2 className="text-3xl font-bold mb-4 text-gray-800 flex items-center gap-2">
                    <Heart className="w-8 h-8 text-accent" fill="currentColor" />
                    Beneficios
                  </h2>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    {job.benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <div className="bg-white p-8 rounded-2xl shadow-xl mb-6 sticky top-4 border-2 border-accent/20">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Detalles del Puesto</h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 mr-2 mt-0.5 text-accent" />
                    <div>
                      <p className="font-semibold">Ubicación</p>
                      <p>{job.location}, {job.country}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Briefcase className="w-5 h-5 mr-2 mt-0.5 text-primary" />
                    <div>
                      <p className="font-semibold">Tipo de Empleo</p>
                      <p>{getTypeLabel(job.type)}</p>
                    </div>
                  </div>

                  {job.salary && (
                    <div className="flex items-start">
                      <span className="mr-2 mt-0.5 text-accent">€</span>
                      <div>
                        <p className="font-semibold">Salario</p>
                        <p>{job.salary}</p>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full mt-6 bg-accent hover:bg-accent-dark text-white px-6 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all shadow-lg flex items-center justify-center space-x-2"
                >
                  <Star className="w-6 h-6" fill="currentColor" />
                  <span>¡Solicitar Ahora!</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;